/**
 * mSTEM Save Project Modal container (PRJ-03 / LLD UC 08).
 *
 * Owns local form state (name/error/submitting) and orchestrates the
 * first-save submit flow:
 *
 *   1. Validate name synchronously. On error, render inline below input.
 *   2. Snapshot the VM via vm.toJSON(); guard against vmState >
 *      VM_STATE_MAX_BYTES (500 KB) before attempting persist.
 *   3. Build a fresh ProjectEntry via mstem-project-storage.createMyProject
 *      and attach the vmState snapshot.
 *   4. Persist to localStorage BEFORE dispatching the reducer (Phase 2
 *      Pattern 2 — mitigates redux-throttle 300ms coalesce race). Quota
 *      errors surface via onShowQuotaFull alert + modal stays open.
 *   5. Dispatch createProjectEntry + setCurrentProject + setProjectTitle.
 *   6. Show projectSaved toast + close modal.
 *
 * Validation rules (T-03-04-03 — sourced from LLD UC 08 + reducer
 * invariants in src/reducers/my-projects.js):
 *   - name: trimmed non-empty, ≤ 100 chars, unique within existingNames
 *
 * Note: subsequent quick-saves (Ctrl+S on an already-named project) bypass
 * this modal entirely — Wave 5 menu-bar handler calls
 * lib/mstem-save-handler.performSave directly. This modal is only opened
 * when performSave returns {status: 'NEED_MODAL'} (first save or the
 * currentId entry was deleted externally).
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';
import VM from 'openblock-vm';

import {closeSaveProjectModal} from '../reducers/modals';
import {createProjectEntry, setCurrentProject} from '../reducers/my-projects';
import {setProjectTitle} from '../reducers/project-title';
import {showAlertWithTimeout} from '../reducers/alerts';
import {createMyProject, writeMyProjects, VM_STATE_MAX_BYTES} from '../lib/mstem-project-storage';
import log from '../lib/log';

import SaveProjectModalComponent from '../components/save-project-modal/save-project-modal.jsx';

class SaveProjectModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChangeName',
            'handleBlurName',
            'handleSubmit',
            'handleCancel',
            'validateName'
        ]);
        this.state = {
            name: props.defaultName || '',
            error: null,
            submitting: false
        };
    }
    validateName (name) {
        const trimmed = (name || '').trim();
        if (!trimmed) return 'errNameRequired';
        if (trimmed.length > 100) return 'errNameTooLong';
        if (this.props.existingNames.indexOf(trimmed) >= 0) return 'errNameDuplicate';
        return null;
    }
    handleChangeName (e) {
        this.setState({name: e.target.value, error: null});
    }
    handleBlurName () {
        this.setState({error: this.validateName(this.state.name)});
    }
    handleSubmit (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (this.state.submitting) return;
        const error = this.validateName(this.state.name);
        if (error) {
            this.setState({error: error});
            return;
        }
        this.setState({submitting: true});

        let vmState = null;
        try {
            vmState = this.props.vm.toJSON();
        } catch (err) {
            log.warn('SaveProjectModal vm.toJSON error', err);
            this.props.onShowSaveError();
            this.setState({submitting: false});
            return;
        }

        if (typeof vmState === 'string' && vmState.length > VM_STATE_MAX_BYTES) {
            this.props.onShowTooLarge();
            this.setState({submitting: false});
            return;
        }

        const trimmedName = this.state.name.trim();
        const entry = createMyProject({
            name: trimmedName,
            type: 'none',
            description: ''
        });
        entry.vmState = vmState;
        const nextList = this.props.projectsList.concat([entry]);

        // Persist BEFORE dispatch — see file header note (redux-throttle race
        // mitigation, mirrors Phase 2 robot-manager-modal.persistAfterDispatch).
        let quotaHit = false;
        const ok = writeMyProjects(nextList, () => {
            quotaHit = true;
        });
        if (!ok || quotaHit) {
            this.props.onShowQuotaFull();
            this.setState({submitting: false});
            return;
        }
        this.props.onCreateEntry(entry);
        this.props.onSetCurrent(entry.id);
        this.props.onSetProjectTitle(trimmedName);
        this.props.onShowSaved();
        this.props.onClose();
    }
    handleCancel () {
        this.props.onClose();
    }
    render () {
        return (
            <SaveProjectModalComponent
                error={this.state.error}
                name={this.state.name}
                onBlurName={this.handleBlurName}
                onCancel={this.handleCancel}
                onChangeName={this.handleChangeName}
                onSubmit={this.handleSubmit}
            />
        );
    }
}

SaveProjectModal.propTypes = {
    defaultName: PropTypes.string,
    existingNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateEntry: PropTypes.func.isRequired,
    onSetCurrent: PropTypes.func.isRequired,
    onSetProjectTitle: PropTypes.func.isRequired,
    onShowQuotaFull: PropTypes.func.isRequired,
    onShowSaveError: PropTypes.func.isRequired,
    onShowSaved: PropTypes.func.isRequired,
    onShowTooLarge: PropTypes.func.isRequired,
    projectsList: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    vm: PropTypes.instanceOf(VM).isRequired
};

SaveProjectModal.defaultProps = {
    defaultName: ''
};

const mapStateToProps = state => ({
    defaultName: state.scratchGui.projectTitle,
    existingNames: state.scratchGui.myProjects.list.map(p => p.name),
    projectsList: state.scratchGui.myProjects.list,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onClose: () => dispatch(closeSaveProjectModal()),
    onCreateEntry: entry => dispatch(createProjectEntry(entry)),
    onSetCurrent: id => dispatch(setCurrentProject(id)),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    onShowSaved: () => showAlertWithTimeout(dispatch, 'projectSaved'),
    onShowSaveError: () => showAlertWithTimeout(dispatch, 'projectSaveError'),
    onShowTooLarge: () => showAlertWithTimeout(dispatch, 'projectTooLarge'),
    onShowQuotaFull: () => showAlertWithTimeout(dispatch, 'localStorageFull')
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(SaveProjectModal);
